/**
 * Role extraction helper extracted from `ZitadelProvider` so React
 * Fast Refresh can hot-reload the provider file without re-running
 * the test-only helper.
 *
 * Two shapes:
 * - **Mock users** carry their roles as a plain `roles: RoleToken[]`
 *   array on the user object itself.
 * - **Real (OIDC) users** carry them inside the
 *   `ROLE_CLAIM` (urn:zitadel:iam:org:project:roles) claim on the
 *   user profile.
 *
 * The mock auth flow was silently returning `[]` because the
 * OIDC-style claim path didn't recognise the plain-array shape,
 * which made mock-mode operators show up as "unauthenticated"
 * everywhere `useHighestRole()` was consulted.
 */
import { ALL_ROLES, type RoleToken } from './types';

export const ROLE_CLAIM = 'urn:zitadel:iam:org:project:roles';

export interface MockAuthUserLike {
  // Allow either an OIDC user shape (`profile` + optional nested
  // claim), a plain mock-user shape (`roles` array directly on the
  // user object), or both. Cast at the call site if you need the
  // narrower mock-user type for tests.
  roles?: RoleToken[] | unknown;
  profile?: { [key: string]: unknown };
  [key: string]: unknown;
}

// Reused from `./types` rather than re-declared here -- this used to be
// an independent Set literal duplicating types.ts's ALL_ROLES array,
// which is exactly the kind of two-copies-can-drift bug the support_l2/
// support_l3 addition would otherwise have silently reintroduced (add a
// role in one file, forget the other, and isRoleToken() rejects a real
// token from a real support-console JWT).
const ALL_ROLES_SET: ReadonlySet<RoleToken> = new Set<RoleToken>(ALL_ROLES);

export function isRoleToken(value: string): value is RoleToken {
  return ALL_ROLES_SET.has(value as RoleToken);
}

export function extractRoles(user: unknown): RoleToken[] {
  if (!user || typeof user !== 'object') return [];
  const u = user as MockAuthUserLike;
  if (Array.isArray(u.roles)) {
    return (u.roles as unknown[])
      .filter((r): r is RoleToken => isRoleToken(String(r)));
  }
  const raw = u.profile?.[ROLE_CLAIM];
  if (!raw || typeof raw !== 'object') return [];
  const out: RoleToken[] = [];
  for (const key of Object.keys(raw as Record<string, unknown>)) {
    const fullKey = key.includes(':') ? key : `nexsoss:${key}`;
    if (isRoleToken(fullKey)) {
      out.push(fullKey);
    }
  }
  return out;
}
